import { audioContext } from "./context.js";

export interface LoadProgress {
  /** 0..1 for the download, or null when the server sends no content-length. */
  downloaded: number | null;
  decoding: boolean;
}

export class LoadCancelled extends Error {
  constructor() {
    super("load cancelled");
    this.name = "LoadCancelled";
  }
}

async function download(
  url: string,
  signal: AbortSignal,
  onProgress: (p: LoadProgress) => void,
): Promise<ArrayBuffer> {
  const res = await fetch(url, { signal });
  if (!res.ok) {
    throw new Error(`${res.status} ${res.statusText}`);
  }

  const total = Number(res.headers.get("content-length") ?? 0);
  if (!res.body || !Number.isFinite(total) || total <= 0) {
    onProgress({ downloaded: null, decoding: false });
    return await res.arrayBuffer();
  }

  const reader = res.body.getReader();
  const chunks: Uint8Array[] = [];
  let received = 0;

  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    chunks.push(value);
    received += value.length;
    onProgress({ downloaded: received / total, decoding: false });
  }

  const merged = new Uint8Array(received);
  let position = 0;
  for (const chunk of chunks) {
    merged.set(chunk, position);
    position += chunk.length;
  }
  return merged.buffer;
}

export interface SegmentRequest {
  /** Track-time second the buffer will begin at. */
  start: number;
  duration: number;
}

/**
 * Fetch and decode a track, or a region of one, to an AudioBuffer (P9).
 *
 * Decoding is what costs the memory - roughly 23 MB per minute at 48 kHz
 * stereo - so callers are responsible for dropping the result promptly, and
 * for asking only for the region they intend to play.
 */
export async function loadTrackBuffer(
  trackId: number,
  signal: AbortSignal,
  onProgress: (p: LoadProgress) => void,
  segment?: SegmentRequest,
): Promise<AudioBuffer> {
  // A region rather than the whole file where one is asked for. This library is
  // long-form - a 48-minute set is 51 MB and over a gigabyte decoded - so for
  // unattended play the difference is the difference between working and not.
  const query = segment
    ? `?start=${segment.start.toFixed(3)}&duration=${segment.duration.toFixed(3)}`
    : "";
  const encoded = await download(`/api/tracks/${trackId}/audio${query}`, signal, onProgress);
  if (signal.aborted) throw new LoadCancelled();

  onProgress({ downloaded: 1, decoding: true });
  // decodeAudioData detaches the ArrayBuffer, so the encoded copy is released
  // as soon as decoding starts rather than lingering beside the decoded one.
  const buffer = await audioContext().decodeAudioData(encoded);
  if (signal.aborted) throw new LoadCancelled();
  return buffer;
}
